import { useOptimisticallyUpdateQueriesWithPredicate, useOptimisticallyUpdateInfiniteQueriesWithPredicate, upsertItemInArrayById } from '@fabric-app/queries/utilities';
import { isTruthy } from '@fabric-app/utils/guards';
import { useQueryClient } from '@tanstack/react-query';
import { resourceQueryPredicates } from '../../resources/queries/resourceQueryPredicates.js';
import { commentQueryPredicates } from '../queries/commentQueryPredicates.js';
import { mutateResourcePinnedComment } from './mutateResourcePinnedComment.js';

const useQueryCacheCommentHelpers = () => {
  const queryClient = useQueryClient();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const optimisticallyUpdateInfiniteQueries = useOptimisticallyUpdateInfiniteQueriesWithPredicate();
  return {
    updateCachedComment: (commentWithUpdatedProperties, mutator) => {
      queryClient.cancelQueries({
        predicate: (q) => commentQueryPredicates.commentByResourceId(q, commentWithUpdatedProperties.resourceId) || resourceQueryPredicates.resourceDetail(q, commentWithUpdatedProperties.resourceId) || resourceQueryPredicates.resourceListAny(q),
        type: "active"
      });
      const optimisticallyUpdateCommentQueries = optimisticallyUpdateQueries((q) => commentQueryPredicates.commentByResourceId(q, commentWithUpdatedProperties.resourceId), (data) => mutator ? data?.map((comment) => comment.id === commentWithUpdatedProperties.id ? mutator(comment) : comment).filter(isTruthy) : upsertItemInArrayById(data ?? [], commentWithUpdatedProperties, {
        createIfNotFound: false
      }));
      const optimisticallyUpdateResourceQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, commentWithUpdatedProperties.resourceId), (resource) => (resource?.commentPinned && mutateResourcePinnedComment(resource, {
        ...resource.commentPinned,
        ...commentWithUpdatedProperties,
        ...mutator?.(resource.commentPinned)
      })) ?? resource);
      const optimisticallyUpdateFilterAndSearchAllQueries = optimisticallyUpdateInfiniteQueries({
        predicate: resourceQueryPredicates.resourceList,
        pageUpdater: (page) => ({
          ...page,
          resources: page.resources.map((resource) => resource.id === commentWithUpdatedProperties.resourceId && resource.commentPinned ? mutateResourcePinnedComment(resource, {
            ...resource.commentPinned,
            ...commentWithUpdatedProperties,
            ...mutator?.(resource.commentPinned)
          }) : resource)
        })
      });
      return {
        optimisticallyUpdateCommentQueries,
        optimisticallyUpdateResourceQueries,
        optimisticallyUpdateFilterAndSearchAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateCommentQueries.resetQueriesData();
          optimisticallyUpdateResourceQueries.resetQueriesData();
          optimisticallyUpdateFilterAndSearchAllQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateCommentQueries.invalidateQueries();
          optimisticallyUpdateResourceQueries.invalidateQueries();
          optimisticallyUpdateFilterAndSearchAllQueries.invalidateQueries();
        }
      };
    },
    deleteCachedComment: (commentId, resourceId) => {
      queryClient.cancelQueries({
        predicate: (q) => commentQueryPredicates.commentByResourceId(q, resourceId) || resourceQueryPredicates.resourceDetail(q, resourceId) || resourceQueryPredicates.resourceListAny(q),
        type: "active"
      });
      const optimisticallyUpdateCommentQueries = optimisticallyUpdateQueries((q) => commentQueryPredicates.commentByResourceId(q, resourceId), (data) => data?.filter((comment) => comment.id !== commentId));
      const optimisticallyUpdateResourceQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, resourceId), (resource) => resource && resource.commentPinned?.id === commentId ? {
        ...resource,
        commentPinned: null,
        // not accurate, what if removing comment which is not pinned?
        commentCount: Math.max(0, (resource.commentCount ?? 0) - 1)
      } : resource);
      const optimisticallyUpdateFilterAndSearchAllQueries = optimisticallyUpdateInfiniteQueries({
        predicate: resourceQueryPredicates.resourceListAny,
        pageUpdater: (page) => {
          return {
            ...page,
            results: page.resources.map((resource) => resource.id === resourceId ? {
              ...resource,
              commentPinned: resource.commentPinned?.id === commentId ? null : resource.commentPinned,
              // not accurate, what if removing comment which is not pinned?
              commentCount: Math.max(0, (resource.commentCount ?? 0) - 1)
            } : resource)
          };
        }
      });
      return {
        optimisticallyUpdateCommentQueries,
        optimisticallyUpdateResourceQueries,
        optimisticallyUpdateFilterAndSearchAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateCommentQueries.resetQueriesData();
          optimisticallyUpdateResourceQueries.resetQueriesData();
          optimisticallyUpdateFilterAndSearchAllQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateCommentQueries.invalidateQueries();
          optimisticallyUpdateResourceQueries.invalidateQueries();
          optimisticallyUpdateFilterAndSearchAllQueries.invalidateQueries();
        }
      };
    },
    addCachedComment: (comment) => {
      queryClient.cancelQueries({
        predicate: (q) => commentQueryPredicates.commentByResourceId(q, comment.resourceId) || resourceQueryPredicates.resourceDetail(q, comment.resourceId) || resourceQueryPredicates.resourceListAny(q),
        type: "active"
      });
      const optimisticallyUpdateCommentQueries = optimisticallyUpdateQueries((q) => commentQueryPredicates.commentByResourceId(q, comment.resourceId), (data) => upsertItemInArrayById(data ?? [], comment, {
        createIfNotFound: true
      }));
      const optimisticallyUpdateResourceQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, comment.resourceId), (resource) => resource ? mutateResourcePinnedComment(resource, comment) : resource);
      const optimisticallyUpdateFilterAndSearchAllQueries = optimisticallyUpdateInfiniteQueries({
        predicate: resourceQueryPredicates.resourceList,
        pageUpdater: (page) => ({
          ...page,
          resources: page.resources.map((resource) => resource.id === comment.resourceId ? mutateResourcePinnedComment(resource, comment) : resource)
        })
      });
      return {
        optimisticallyUpdateCommentQueries,
        optimisticallyUpdateResourceQueries,
        optimisticallyUpdateFilterAndSearchAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateCommentQueries.resetQueriesData();
          optimisticallyUpdateResourceQueries.resetQueriesData();
          optimisticallyUpdateFilterAndSearchAllQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateCommentQueries.invalidateQueries();
          optimisticallyUpdateResourceQueries.invalidateQueries();
          optimisticallyUpdateFilterAndSearchAllQueries.invalidateQueries();
        }
      };
    },
    replaceCommentInCache: (oldComment, newComment) => {
      queryClient.cancelQueries({
        predicate: (q) => commentQueryPredicates.commentByResourceId(q, oldComment.resourceId) || resourceQueryPredicates.resourceDetail(q, oldComment.resourceId) || resourceQueryPredicates.resourceListAny(q),
        type: "active"
      });
      const optimisticallyUpdateCommentQueries = optimisticallyUpdateQueries((q) => commentQueryPredicates.commentByResourceId(q, oldComment.resourceId), (data) => (data ?? []).map((comment) => comment.id === oldComment.id ? newComment : comment));
      const optimisticallyUpdateResourceQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, oldComment.resourceId), (resource) => (resource?.commentPinned && mutateResourcePinnedComment(resource, newComment)) ?? resource);
      const optimisticallyUpdateFilterAndSearchAllQueries = optimisticallyUpdateInfiniteQueries({
        predicate: resourceQueryPredicates.resourceList,
        pageUpdater: (page) => ({
          ...page,
          resources: page.resources.map((resource) => resource.id === oldComment.resourceId && resource.commentPinned ? mutateResourcePinnedComment(resource, newComment) : resource)
        })
      });
      return {
        optimisticallyUpdateCommentQueries,
        optimisticallyUpdateResourceQueries,
        optimisticallyUpdateFilterAndSearchAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateCommentQueries.resetQueriesData();
          optimisticallyUpdateResourceQueries.resetQueriesData();
          optimisticallyUpdateFilterAndSearchAllQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateCommentQueries.invalidateQueries();
          optimisticallyUpdateResourceQueries.invalidateQueries();
          optimisticallyUpdateFilterAndSearchAllQueries.invalidateQueries();
        }
      };
    }
  };
};

export { useQueryCacheCommentHelpers };
//# sourceMappingURL=useQueryCacheCommentHelpers.js.map
